import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    CardMedia,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    Box,
    Button,
    Typography,
    Card,
    CardContent
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { SaleItem } from "../../models/saleItem";
import { FullProperty } from "../../models/fullProperty";
import { SaleItemFormData, saleItemSchema, onSubmitError, handleSaveItem } from "./upsertSaleItemHelpers";
import { DropzoneState, useDropzone } from "react-dropzone";
import { Close, ExpandMore } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { StringsService } from "../../services/strings/strings";
import { ImageUploadBox } from "../../shared/imageUpload";

interface UpsertSaleItemDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    restaurant: FullProperty;
    editingItem: boolean;
    currentItem: SaleItem | null;
    saleItems: SaleItem[];
    setSaleItems: React.Dispatch<React.SetStateAction<SaleItem[]>>;
    possibleDiscountReasons: string[];
    possibleFoodCateory: string[];
    initialThumbnail: string | null;
}

export const UpsertSaleItemDialog: React.FC<UpsertSaleItemDialogProps> = ({
    open,
    onClose,
    onSuccess,
    restaurant,
    editingItem,
    currentItem,
    saleItems,
    setSaleItems,
    possibleDiscountReasons,
    possibleFoodCateory,
    initialThumbnail
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [expandedAccordian, setExpandedAccordian] = useState<boolean>(false);
    const [thumbnailImage, setThumbnailImage] = useState<File | null>(null);
    const [oldThumbnailImage, setOldThumbnailImage] = useState<string | null>(initialThumbnail);

    const { control, handleSubmit, setValue, reset, formState: { errors } } = useForm<SaleItemFormData>({
        resolver: yupResolver(saleItemSchema(restaurant)),
        defaultValues: {
            name: "",
            description: "",
            available: 0,
            originalPrice: 0,
            discountPrice: 0,
            isActive: false,
            startOfCollectionTime: "",
            endDate: "",
            foodCategory: "",
            discountReason: "",
        },
    });

    useEffect(() => {
        const updateViewData = async () => {
            if (currentItem) {
                setLoading(true);
                setValue("name", await StringsService.getStringById(`restaurant-${restaurant.id}-saleItem-${currentItem.id}-sale-item-name`));
                setValue("description", await StringsService.getStringById(`restaurant-${restaurant.id}-saleItem-${currentItem.id}-sale-item-description`));
                setValue("originalPrice", currentItem.originalPrice);
                setValue("discountPrice", currentItem.discountPrice);
                setValue("isActive", currentItem.isActive);
                setValue("available", currentItem.available);
                setValue("startOfCollectionTime", currentItem.startOfCollectionTime);
                setValue("endDate", currentItem.endDate);
                setValue("discountReason", currentItem.discountReason ?? "");
                setValue("foodCategory", currentItem.foodCategory);
                setLoading(false);
            }

        }
        updateViewData()
    }, [currentItem, setValue]);

    const clearAll = () => {
        setExpandedAccordian(false);
        setThumbnailImage(null);
        setOldThumbnailImage(null)
        setValue("name", "");
        setValue("description", "");
        setValue("originalPrice", 0);
        setValue("discountPrice", 0);
        setValue("isActive", false);
        setValue("available", 0);
        setValue("startOfCollectionTime", "");
        setValue("endDate", "");
        setValue("discountReason", "");
        setValue("foodCategory", "");
    }

    const fieldRefs: Record<string, React.RefObject<HTMLDivElement>> = {
        name: useRef(null),
        description: useRef(null),
        foodCategory: useRef(null),
        discountReason: useRef(null),
        endDate: useRef<HTMLDivElement>(null),
        available: useRef(null),
        originalPrice: useRef(null),
        discountPrice: useRef(null),
    };

    const thumbnailProps: DropzoneState = useDropzone({
        accept: { "image/*": [] },
        maxFiles: 1,
        maxSize: 5 * 1024 * 1024, // 5 MB max size
        onDrop: (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                const file = acceptedFiles[0];
                if (file.type.startsWith("image/")) {
                    setOldThumbnailImage(null);
                    setThumbnailImage(file);
                }
            }
        },
    });

    const activeThumbnail = (): string | null => {
        if (thumbnailImage) {
            console.log("thumbnailImage was chosen", URL.createObjectURL(thumbnailImage))
            return URL.createObjectURL(thumbnailImage);
        }
        if (oldThumbnailImage) {
            console.log("oldThumbnailImage was chosen", oldThumbnailImage)
            return oldThumbnailImage;
        }
        console.log("NO ThumbnailImage was chosen")
        return null
    }

    const onSubmit = async (data: SaleItemFormData) => {
        if (currentItem) {
            await handleSaveItem(data, restaurant, currentItem, editingItem, saleItems, setSaleItems, thumbnailImage, setLoading, () => {
                onSuccess()
                onClose()
                clearAll()
            });
        }
    };

    return (
        <Dialog open={open} onClose={() => { onClose(); clearAll(); }}>
            <DialogTitle>
                {editingItem ? "Edit Sale Item" : "Add New Sale Item"}
            </DialogTitle>
            {
                loading ?
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "50vh",
                        }}
                    >
                        <CircularProgress />
                    </Box>
                    :
                    <DialogContent>
                        <Accordion
                            defaultExpanded={!editingItem}
                            onChange={(_, expanded) => setExpandedAccordian(expanded)}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">Bag Details</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Name"
                                            inputRef={fieldRefs.name}
                                            margin="normal"
                                            error={!!errors.name}
                                            helperText={errors.name?.message}
                                        />
                                    )}
                                />

                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            inputRef={fieldRefs.description}
                                            label="Description"
                                            error={!!errors.description}
                                            helperText={errors.description?.message}
                                            margin="normal"
                                            multiline
                                            rows={3}
                                        />
                                    )}
                                />
                                <FormControl fullWidth error={!!errors.discountReason} margin="normal">
                                    <InputLabel id="discount-reason-label">Discount Reason</InputLabel>
                                    <Controller
                                        name="discountReason"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <Select
                                                labelId="discount-reason-label"
                                                inputRef={fieldRefs.discountReason}
                                                label="Discount Reason"
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            >
                                                {possibleDiscountReasons.map((reason) => (
                                                    <MenuItem key={reason} value={reason}>
                                                        {reason}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.discountReason && (
                                        <FormHelperText>{errors.discountReason.message}</FormHelperText>
                                    )}
                                </FormControl>
                                <FormControl fullWidth error={!!errors.foodCategory} margin="normal">
                                    <InputLabel id="food-category-label">Food Category</InputLabel>
                                    <Controller
                                        name="foodCategory"
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <Select
                                                labelId="food-category-label"
                                                label="Food Category"
                                                inputRef={fieldRefs.foodCategory}
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            >
                                                {possibleFoodCateory.map((requirement) => (
                                                    <MenuItem key={requirement} value={requirement}>
                                                        {requirement}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    {errors.foodCategory && (
                                        <FormHelperText>{errors.foodCategory.message}</FormHelperText>
                                    )}
                                </FormControl>
                                <ImageUploadBox
                                    activeThumbnail={activeThumbnail}
                                    imageState={thumbnailProps}
                                    onDelete={() => {
                                        setThumbnailImage(null);
                                        setOldThumbnailImage(null);
                                    }}
                                />

                            </AccordionDetails>

                        </Accordion>

                        <Box
                            display="flex"
                            flexDirection={{ xs: 'column', md: 'row' }}
                            gap={1}
                        >
                            <Controller
                                name="startOfCollectionTime"
                                control={control}
                                render={({ field }) => (
                                    <Box ref={fieldRefs.startOfCollectionTime} flex="1">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                label="Start"
                                                value={dayjs(currentItem?.startOfCollectionTime)}
                                                onAccept={(newValue: Dayjs | null) => {
                                                    const formattedString = newValue
                                                        ? newValue.toDate().toISOString()
                                                        : "";
                                                    field.onChange(formattedString);
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        error: !!errors.startOfCollectionTime,
                                                        helperText: errors.startOfCollectionTime?.message,
                                                        margin: "normal",
                                                        InputProps: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <AccessTimeIcon />
                                                                </InputAdornment>
                                                            ),
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                )}
                            />

                            <Controller
                                name="endDate"
                                control={control}
                                render={({ field }) => (
                                    <Box ref={fieldRefs.endDate} flex="1">
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateTimePicker
                                                label="End"
                                                value={dayjs(currentItem?.endDate)}
                                                onAccept={(newValue: Dayjs | null) => {
                                                    const formattedString = newValue
                                                        ? newValue.toDate().toISOString()
                                                        : "";
                                                    field.onChange(formattedString);
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        error: !!errors.endDate,
                                                        helperText: errors.endDate?.message,
                                                        margin: "normal",
                                                        InputProps: {
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <AccessTimeIcon />
                                                                </InputAdornment>
                                                            ),
                                                        },
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Box>
                                )}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                            }}
                        >

                            <Box sx={{ flex: 1 }}>
                                <Controller
                                    name="originalPrice"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            inputRef={fieldRefs.originalPrice}
                                            error={!!errors.originalPrice}
                                            helperText={errors.originalPrice?.message}
                                            fullWidth
                                            label="Original Price (R)"
                                            type="number"
                                            value={field.value / 100}
                                            onChange={(e) => {
                                                const convertedValue = parseFloat(e.target.value) * 100;
                                                field.onChange(convertedValue);
                                            }}
                                            margin="normal"
                                        />
                                    )}
                                />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                                <Controller
                                    name="discountPrice"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            inputRef={fieldRefs.discountPrice}
                                            error={!!errors.discountPrice}
                                            helperText={errors.discountPrice?.message}
                                            fullWidth
                                            label="Discount Price (R)"
                                            type="number"
                                            value={field.value / 100}
                                            onChange={(e) => {
                                                const convertedValue = parseFloat(e.target.value) * 100;
                                                field.onChange(convertedValue);
                                            }}
                                            margin="normal"
                                        />
                                    )}
                                />
                            </Box>
                        </Box>

                        <Controller
                            name="available"
                            control={control}
                            render={({ field }) => (
                                <Box sx={{ margin: "0 8px" }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ color: "#162C1F", fontWeight: "bold", mb: 1 }}
                                    >
                                        Available Bags
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                            onClick={() => {
                                                const newValue = Math.max(Number(field.value || 0) - 1, 0);
                                                field.onChange(newValue);
                                            }}
                                            sx={{
                                                backgroundColor: "#F0F2F1",
                                                '&:hover': { backgroundColor: "#D0D5D2" },
                                                borderRadius: 1
                                            }}
                                        >
                                            <RemoveIcon />
                                        </IconButton>

                                        <TextField
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            value={field.value === undefined || field.value === null ? '' : field.value}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '') {
                                                    field.onChange('');
                                                    return;
                                                }

                                                const numericValue = parseInt(value, 10);
                                                if (!isNaN(numericValue) && numericValue >= 0) {
                                                    field.onChange(numericValue);
                                                }
                                            }}
                                            sx={{ width: 80 }}
                                        />

                                        <IconButton
                                            onClick={() => {
                                                const newValue = Number(field.value || 0) + 1;
                                                field.onChange(newValue);
                                            }}
                                            sx={{
                                                backgroundColor: "#F0F2F1",
                                                '&:hover': { backgroundColor: "#D0D5D2" },
                                                borderRadius: 1
                                            }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                        />
                        <FormControlLabel
                            control={
                                <Controller
                                    name="isActive"
                                    control={control}
                                    render={({ field }) => (
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                            sx={{
                                                color: "#757575",
                                                "&.Mui-checked": { color: "#162C1F" },
                                                "&.MuiCheckbox-root:hover": { backgroundColor: "#E6F0E8" },
                                            }}
                                        />
                                    )}
                                />
                            }
                            label="Publish"
                        />

                    </DialogContent>
            }

            {
                !loading &&
                <DialogActions>
                    <Button
                        onClick={() => { onClose(); clearAll(); }}
                        variant="outlined"
                        sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px" }}
                    >
                        <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                            Cancel
                        </Typography>
                    </Button>
                    <Button
                        onClick={handleSubmit(onSubmit, (errors) => onSubmitError(errors, fieldRefs))}
                        type="submit"
                        variant="contained"
                        sx={{ margin: "0 8px", background: "#162C1F", border: "1px solid #162C1F", borderRadius: "24px" }}
                    >
                        <Typography variant="button" component="h1" sx={{ color: "ECE8D5" }}>
                            Save
                        </Typography>
                    </Button>
                </DialogActions>
            }

        </Dialog>
    );
};